// Right body column for Today view (desktop lg+).
// Renders: Ghi nhanh Inbox · Thói quen hôm nay · Tài chính mini · Ghi chú ghim.
// A5: Habit check-in strip (inline toggle via api.actions.toggleHabit).
// B11: Finance lines grouped under "Tài chính tháng" header.
import { AlertTriangle, CheckCircle2, Circle, Inbox, Pin } from "lucide-react";
import { formatMoney, todayIso } from "../lib/date";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { EntityKind } from "./entity-form";
import type { BudgetStatus } from "./bento-derive";
import { moneyShort, Tile, TileHead } from "./bento-ui";

const link = "text-[12px] font-bold text-brand-600";

interface FinanceSnapshot {
  spentToday: number;
  budgetRemaining: number;
  overBudget: BudgetStatus[];
}

interface Props {
  api: LifeOsApi;
  finance: FinanceSnapshot;
  onGoto: (v: string) => void;
  onForm: (kind: EntityKind, item?: Record<string, unknown>) => void;
}

export function TodayRightColumn({ api, finance, onGoto, onForm }: Props) {
  const pinnedNote = api.state.notes.filter((n) => n.pinned)[0];
  const today = todayIso();

  return (
    <div className="col-span-2 flex flex-col gap-3.5">
      {/* Ghi nhanh vào Inbox */}
      {/* to-[#fce7f3] là hex cứng — cần dark: variant; brand-100 tự đổi theo theme */}
      <div className="rounded-3xl bg-gradient-to-br from-brand-100 to-[#fce7f3] dark:to-pink-500/15 p-5">
        <div className="mb-2 flex items-center gap-2 text-brand-600">
          <Inbox className="h-4 w-4" />
          <span className="text-[11px] font-bold uppercase tracking-widest">Ghi nhanh vào Inbox</span>
        </div>
        <input
          className="w-full rounded-xl border border-white/70 bg-white/80 px-3 py-2.5 text-[13px] outline-none placeholder:text-ink-500"
          placeholder="Quăng nhanh việc / ý tưởng…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.currentTarget.value.trim()) {
              api.actions.addInboxItem({ text: e.currentTarget.value.trim() });
              e.currentTarget.value = "";
            }
          }}
        />
        <button className="mt-2 text-[12px] text-ink-700" onClick={() => onGoto("inbox")}>
          Có <b>{api.state.inbox.length}</b> mục chờ phân loại →
        </button>
      </div>

      {/* A5: Thói quen hôm nay — compact inline toggle strip */}
      {api.state.habits.length > 0 && (
        <Tile>
          <TileHead
            title="Thói quen hôm nay"
            action={<button className={link} onClick={() => onGoto("habits")}>Tất cả</button>}
          />
          <div className="space-y-2">
            {api.state.habits.map((h) => {
              const done = h.completions.includes(today);
              return (
                <button
                  key={h.id}
                  onClick={() => api.actions.toggleHabit(h.id, today)}
                  className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left transition ${
                    done ? "bg-emerald-50 dark:bg-emerald-500/10" : "bg-ink-100/40 hover:bg-ink-100/70"
                  }`}
                  aria-pressed={done}
                  aria-label={`${done ? "Bỏ hoàn thành" : "Hoàn thành"}: ${h.name}`}
                >
                  {done ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  ) : (
                    <Circle className="h-4 w-4 shrink-0 text-ink-300" />
                  )}
                  <span
                    className={`flex-1 text-[13px] font-semibold ${done ? "text-emerald-700 line-through dark:text-emerald-300" : ""}`}
                  >
                    {h.name}
                  </span>
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: h.color }}
                  />
                </button>
              );
            })}
          </div>
        </Tile>
      )}

      {/* B11: Tài chính tháng — grouped under labeled header to avoid ambiguity.
          Secondary on mobile → hidden (full snapshot lives in the Finance tab). */}
      <Tile className="hidden lg:block">
        <TileHead
          title="Tài chính tháng"
          action={
            <div className="flex items-center gap-2">
              <button
                className="rounded-lg bg-brand-100 px-2.5 py-1 text-[11px] font-bold text-brand-600"
                onClick={() => onForm("transaction")}
              >
                + Ghi chi
              </button>
              <button className={link} onClick={() => onGoto("finance")}>Chi tiết</button>
            </div>
          }
        />
        <div className="space-y-2.5 text-[13px]">
          <Line label="Chi hôm nay" value={formatMoney(finance.spentToday)} />
          {/* "Còn lại ngân sách tháng" — distinct from account balance shown in Finance view */}
          <Line
            label="Còn lại ngân sách tháng"
            value={formatMoney(finance.budgetRemaining)}
            valueClass="text-emerald-600"
          />
          {finance.overBudget.map((b) => (
            <div
              key={b.budget.id}
              className="flex items-center justify-between rounded-xl bg-rose-50 px-3 py-2 dark:bg-rose-500/10"
            >
              <span className="flex items-center gap-1.5 text-rose-600 dark:text-rose-300">
                <AlertTriangle className="h-3.5 w-3.5" />
                Vượt: {b.budget.category}
              </span>
              <b className="text-rose-600">+{moneyShort(b.spent - b.budget.monthlyLimit)}</b>
            </div>
          ))}
        </div>
      </Tile>

      {/* Ghi chú ghim — first pinned note only. Secondary on mobile → hidden. */}
      {pinnedNote && (
        <div className="hidden rounded-3xl bg-white p-5 lg:block">
          <div className="mb-1 flex items-center gap-2 text-brand-600">
            <Pin className="h-4 w-4 fill-brand-500" />
            <span className="text-[11px] font-bold uppercase tracking-widest">Ghi chú ghim</span>
          </div>
          <p className="font-display text-[16px] font-bold">{pinnedNote.title}</p>
          {/* whitespace-pre-line honours the note's line breaks; clamp keeps the card compact. */}
          <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-ink-700 line-clamp-[12]">{pinnedNote.body}</p>
        </div>
      )}
    </div>
  );
}

// ── Local helper ─────────────────────────────────────────────────────────────

function Line({ label, value, valueClass = "" }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink-500">{label}</span>
      <b className={valueClass}>{value}</b>
    </div>
  );
}
